import { useEffect, useRef } from 'react';

import { useMixpanel } from 'context/mixpanel-context';
import { useLocation } from 'react-router-dom';

// Track 20% of subsequent page views in a session
const SUBSEQUENT_PAGE_SAMPLE_RATE = 0.2;

export const usePageTracking = (): void => {
  const location = useLocation();
  const { track } = useMixpanel();

  const enterTime = useRef<number>(Date.now());
  const previousPath = useRef<string | null>(null);
  const lastTrackedPath = useRef<string | null>(null);
  const hasTrackedInSession = useRef<boolean>(false);

  useEffect(() => {
    const currentPath = location.pathname;
    const currentTimestamp = Date.now();

    if (currentPath === lastTrackedPath.current) {
      return;
    }

    let durationOnPreviousPage: number | undefined;
    if (previousPath.current) {
      durationOnPreviousPage = Math.round((currentTimestamp - enterTime.current) / 1000);
    }

    // Strategy: Always track first page view in session since active user metrics depends on it, sample subsequent ones
    const isFirstPageView = !hasTrackedInSession.current;
    const shouldTrackSubsequent = Math.random() < SUBSEQUENT_PAGE_SAMPLE_RATE;
    const shouldTrack = isFirstPageView || shouldTrackSubsequent;

    if (shouldTrack) {
      const pageViewProperties: Record<string, any> = {
        path: currentPath,
        is_first_in_session: isFirstPageView,
      };

      if (durationOnPreviousPage !== undefined && previousPath.current) {
        pageViewProperties.previous_path = previousPath.current;
        pageViewProperties.previous_page_duration_seconds = durationOnPreviousPage;
      }

      track('app_page_viewed', pageViewProperties);
      hasTrackedInSession.current = true;
    }

    // Always update refs (even if we didn't track)
    previousPath.current = lastTrackedPath.current;
    lastTrackedPath.current = currentPath;
    enterTime.current = currentTimestamp;
  }, [location.pathname, track]);
};
